import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { checkIsAdmin } from "@/lib/admin.functions";
import { ContextSwitcher } from "@/components/context-switcher";
import { useAppContexts } from "@/hooks/use-app-context";

type Role = "runner" | "organizer" | "cafe";

export function BruloHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState<Role | null>(null);
  const { active: activeCtx, managed } = useAppContexts();
  const fetchIsAdmin = useServerFn(checkIsAdmin);
  const { data: adminCheck } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: () => fetchIsAdmin(),
    enabled: !!user,
  });
  const isAdmin = !!adminCheck?.isAdmin;

  useEffect(() => {
    if (!user) {
      setRole(null);
      return;
    }
    supabase
      .from("profiles")
      .select("role")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.role) setRole(data.role as Role);
      });
  }, [user]);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Hard redirect to landing so the _authenticated guard doesn't race
    // and bounce the user to /auth before navigation happens.
    window.location.assign("/");
  };

  return (
    <header className="border-b border-border/60 bg-background/80 backdrop-blur sticky top-0 z-40">
      <div className="mx-auto max-w-6xl px-4 md:px-6 min-h-16 py-2 flex items-center justify-between gap-3 flex-wrap">
        <Link to={activeCtx?.kind === "cafe" || (managed.length === 0 && role === "cafe") ? "/cafe/dashboard" : user ? "/dashboard" : "/"} className="font-display text-xl sm:text-2xl tracking-widest">
          BRULO<span className="text-primary">.</span>
        </Link>
        <nav className="flex items-center gap-3 sm:gap-6 text-sm flex-wrap justify-end">
          {user ? (
            <>
              {managed.length > 0 && activeCtx?.kind === "personal" ? (
                <>
                  <Link to="/dashboard" className="hover:text-primary transition">Personal</Link>
                  <Link to="/mapa" className="hover:text-primary transition">Mapa</Link>
                  <Link to="/perfil" className="text-muted-foreground hover:text-foreground transition">Mi perfil</Link>
                </>
              ) : null}
              {managed.length === 0 && role === "runner" && (
                <>
              <Link to="/dashboard" className="hover:text-primary transition">
                Mi club
              </Link>
                  <Link to="/mapa" className="hover:text-primary transition">
                    Mapa
                  </Link>
                  <Link to="/perfil" className="text-muted-foreground hover:text-foreground transition">
                    Mi perfil
                  </Link>
                </>
              )}

              {activeCtx?.kind === "club" && (
                <>
                  <Link to="/dashboard" className="hover:text-primary transition">Mi club</Link>
                  <Link to="/events/new" className="hover:text-primary transition">Nueva salida</Link>
                </>
              )}
              {managed.length === 0 && role === "organizer" && (
                <>
                  <Link to="/dashboard" className="hover:text-primary transition">
                    Mis eventos
                  </Link>
                  <Link to="/events/new" className="hover:text-primary transition">
                    Nueva salida
                  </Link>
                </>
              )}
              {activeCtx?.kind === "cafe" && (
                <>
                  <Link to="/cafe/dashboard" className="hover:text-primary transition">Mi café</Link>
                  <Link to="/cafe/perfil" className="text-muted-foreground hover:text-foreground transition">Mi perfil</Link>
                </>
              )}
              {managed.length === 0 && role === "cafe" && (
                <>
                  <Link to="/cafe/dashboard" className="hover:text-primary transition">
                    Mi café
                  </Link>
                  <Link to="/cafe/perfil" className="text-muted-foreground hover:text-foreground transition">
                    Mi perfil
                  </Link>
                </>
              )}
              {isAdmin && (
                <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary transition uppercase tracking-wider">
                  Admin
                </Link>
              )}
              <ContextSwitcher />
              <button onClick={signOut} className="text-muted-foreground hover:text-foreground">
                Salir
              </button>
            </>
          ) : (
            <Link to="/auth" className="px-4 py-2 min-h-11 inline-flex items-center rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90">
              Acceder
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
